<template>
  <div class="user-menu">
    <i-menu
      v-if="currentUser"
      ref="menuEl"
      :close-on-item-click="true"
      :trigger-align="iMenuTriggerAlignOptions.RIGHT"
      :width="212"
      @i-menu-open-changed="onMenuOpenChanged"
    >
      <button
        slot="trigger"
        data-testid="user-button"
        :class="{
          open: menuOpen === true,
          collapsed,
          'i-px-8': !collapsed,
          'i-py-6': !collapsed,
        }"
        class="user-button"
        @mouseenter="hovering = true"
        @mouseleave="hovering = ''"
      >
        <div
          :class="{ 'align-items-center justify-content-center': collapsed }"
          class="user-container d-flex flex-row align-items-center"
        >
          <user-avatar
            :user="currentUser"
            :class="{ 'i-mr-4': !collapsed }"
          ></user-avatar>
          <div class="user-data d-flex flex-column align-items-start">
            <div
              class="name data i-text-neutral-120 i-text-button-3"
              :title="username"
            >
              {{ username }}
            </div>
            <div
              class="organisation data i-text-neutral-110 i-text-body-3"
              :title="organisation"
            >
              {{ organisation }}
            </div>
          </div>
          <i-icon
            :symbol="menuOpen ? 'small-chevron-up' : 'small-chevron-down'"
            class="menu-icon"
            :colour="
              hovering || menuOpen
                ? palette['light-neutral-90']
                : palette['light-neutral-80']
            "
            :size="16"
          ></i-icon>
        </div>
      </button>

      <i-menu-item
        slot="items"
        label="Account"
        icon="small-user-single"
        data-testid="i-menu-item"
        @item-click="() => openRoute('/settings/account')"
      ></i-menu-item>

      <i-menu-item
        slot="items"
        label="Team"
        icon="small-user-multiple"
        data-testid="i-menu-item"
        @item-click="() => openRoute('/settings/team')"
      ></i-menu-item>

      <i-menu-item
        v-if="hasEditPermissions"
        slot="items"
        label="Invite User"
        icon="small-user-add"
        data-testid="i-menu-item"
        @item-click="() => inviteUser()"
      ></i-menu-item>

      <div slot="items" class="divider"></div>

      <i-menu-item
        slot="items"
        label="Help Center"
        icon="small-help-question-circle"
        data-testid="i-menu-item"
        right-icon="small-arrows-expand"
        :right-icon-color="palette['light-neutral-70']"
        @item-click="() => openExternalLink('https://help.inscribe.ai/en/')"
      ></i-menu-item>

      <i-menu-item
        slot="items"
        label="API Documentation"
        icon="small-programming-browser-code-2"
        data-testid="i-menu-item"
        right-icon="small-arrows-expand"
        :right-icon-color="palette['light-neutral-70']"
        @item-click="() => openExternalLink('https://docs.inscribe.ai')"
      ></i-menu-item>

      <div slot="items" class="divider"></div>

      <!-- workspaces -->
      <i-menu-item
        v-for="ws in firstFiveWorkspaces"
        :key="ws.id"
        slot="items"
        :label="ws.name"
        :checked="ws.isCurrentWorkspace"
        icon="small-dashboard-1"
        data-testid="i-menu-item"
        @item-click="() => switchToWorkspace(ws)"
      ></i-menu-item>

      <i-menu-item
        v-if="tooManyWorkSpaces"
        slot="items"
        label="Switch Workspace"
        icon="small-arrows-reload"
        data-testid="i-menu-item"
        @item-click="() => (showSwitcherModal = true)"
      ></i-menu-item>

      <i-menu-item
        v-if="orgAdminAndCreationEnabled"
        slot="items"
        label="New Workspace"
        icon="small-add-circle"
        data-testid="i-menu-item"
        @item-click="() => showCreateNewWorkspace()"
      ></i-menu-item>

      <div slot="items" class="divider"></div>

      <i-menu-item
        slot="items"
        label="Sign Out"
        icon="small-logout"
        data-testid="i-menu-item"
        @item-click="() => doLogout()"
      ></i-menu-item>
    </i-menu>

    <!-- workspace panel  -->
    <workspace-switcher
      :show-switcher-modal="showSwitcherModal"
      :show-creation-modal="showCreationModal"
      @toggle-switcher-modal="toggleSwitcherModal()"
      @set-creation-modal-open="onSetCreationModalOpen"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import {
  iIcon,
  iMenu,
  iMenuItem,
  iMenuTriggerAlignOptions,
} from '@inscribe/signal';
import palette from '@inscribe/signal/dist/palette';
import UserAvatar from '@/V2/components/UserAvatar';
import WorkspaceSwitcher from './WorkspaceSwitcher';

export default {
  name: 'UserArea',
  components: {
    iIcon,
    iMenu,
    iMenuItem,
    UserAvatar,
    WorkspaceSwitcher,
  },
  props: {
    collapsed: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    iMenuTriggerAlignOptions,
    palette,
    hovering: false,
    menuOpen: false,
    showSwitcherModal: false,
    showCreationModal: false,
  }),
  computed: {
    ...mapGetters({
      orgAdminAndCreationEnabled: 'isOrgAdminAndWorkspacesCreationEnabled',
      workspacesEnabled: 'isWorkspacesEnabled',
      hasEditPermissions: 'isOrgAdmin',
    }),
    ...mapState(['currentUser', 'workspaces']),
    username() {
      if (this.currentUser.first_name || this.currentUser.last_name) {
        return `${this.currentUser.first_name} ${this.currentUser.last_name}`;
      } else {
        return this.currentUser.username;
      }
    },
    organisation() {
      return this.currentUser.organisation?.name || '';
    },
    firstFiveWorkspaces() {
      return this.tooManyWorkSpaces
        ? []
        : this.workspaces
            .map((org) => {
              return {
                name: org.name,
                id: org.id,
                isCurrentWorkspace:
                  org.name === this.currentUser.organisation.name,
                button: true,
              };
            })
            .sort((a, b) => b.isCurrentWorkspace - a.isCurrentWorkspace);
    },
    tooManyWorkSpaces() {
      return this.workspaces.length > 5;
    },
  },
  methods: {
    ...mapActions(['switchWorkspace']),
    openRoute(path) {
      if (this.$route.fullPath !== path) {
        this.$router.push({
          path: path,
        });
      }
    },
    inviteUser() {
      const route = '/settings/team?showInviteModal=true';
      if (!this.$route.fullPath.includes('/settings/team')) {
        this.openRoute(route);
      } else {
        this.openRoute('/');
        setTimeout(() => {
          this.openRoute(route);
        });
      }
    },
    openExternalLink(url) {
      window.open(url, '_blank');
    },
    doLogout() {
      if (confirm('Are you sure you want to log out?')) {
        localStorage.removeItem('inscribeScopeSelectedUserDefault');
        this.$store.dispatch('logout').then(() => {
          this.$router.push({ name: 'login' });
        });
      }
    },
    toggleSwitcherModal() {
      this.showSwitcherModal = !this.showSwitcherModal;
    },
    onMenuOpenChanged(menuOpen) {
      this.menuOpen = menuOpen;
    },
    onSetCreationModalOpen(value) {
      this.showCreationModal = value;
    },
    showCreateNewWorkspace() {
      this.showSwitcherModal = false;
      this.showCreationModal = true;
    },
    switchToWorkspace(workspace) {
      if (!workspace.isCurrentWorkspace) {
        this.switchWorkspace(workspace.id)
          .then((response) => {
            const { redirect_url } = response.data;
            window.location.href = redirect_url || '/';
          })
          .catch(() => {
            this.$notify({
              group: 'general',
              type: 'error',
              title: 'Error switching workspace.',
              duration: 5000,
            });
          });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.user-menu {
  user-select: none;
  .user-button {
    width: 100%;
    height: 56px;
    border: 0;
    border: 1px solid $light-neutral-60;
    background-color: transparent;
    outline: 0;
    border-radius: 12px;
    transition: background-color 150ms ease-in-out;

    &:hover,
    &.open {
      background-color: $light-neutral-default;
    }

    &.collapsed {
      height: 48px;
      width: 48px;
      border-radius: 50%;

      .user-data,
      .menu-icon {
        display: none !important;
      }
    }

    .user-data {
      flex: 1;

      .data {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 112px;
      }
    }
  }
}
</style>
